/**
 * Subcontractor records: what the office tracks for each Keystone subcontractor — contract,
 * fleet driving, personal ID renewals, clothing and pay. Office staff only.
 * Pay terms live on the directory account (bill_payment_terms) because new bills use them.
 */

export type PayStructure = 'not_set' | 'day_rate' | 'price_work' | 'hourly' | 'mixed';

export const PAY_STRUCTURE_LABELS: Record<PayStructure, string> = {
  not_set: 'Not set',
  day_rate: 'Day rate',
  price_work: 'Price work',
  hourly: 'Hourly',
  mixed: 'Mixed',
};

const PAY_STRUCTURES = new Set<PayStructure>(Object.keys(PAY_STRUCTURE_LABELS) as PayStructure[]);

/** Suggestions for the ID type field; anything else can be typed. */
export const PERSONAL_ID_TYPES = [
  'Driving licence',
  'DVLA check code',
  'Passport',
  'Right to work',
  'CSCS card',
  'Proof of address',
  'Public liability insurance',
];

export type PersonalId = {
  id: string;
  type: string;
  reference: string | null;
  obtainedOn: string | null;
  expiresOn: string | null;
  /** How often it has to be renewed or rechecked. Used when there is no expiry date. */
  renewalMonths: number | null;
};

export type SubcontractorRecord = {
  accountId: string;
  contractStartDate: string | null;
  contractEndDate: string | null;
  contractTerm: string | null;
  contractIssued: boolean;
  contractIssuedOn: string | null;
  contractSignedOn: string | null;
  fleetDriver: boolean;
  vehicleRegistration: string | null;
  licenceCheckDate: string | null;
  fleetPolicyIssued: boolean;
  fleetPolicyIssuedOn: string | null;
  fleetPolicyVersion: string | null;
  personalIds: PersonalId[];
  clothingRequirements: string | null;
  payTermsNotes: string | null;
  payStructure: PayStructure;
  payRate: number | null;
  payStructureNotes: string | null;
  notes: string | null;
  updatedAt: string | null;
};

/** The policy version currently issued at van handover. */
export const CURRENT_FLEET_POLICY_VERSION = 'v1.2';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function text(value: unknown): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

function dateOnly(value: unknown): string | null {
  const raw = text(value);
  return raw && DATE_ONLY.test(raw.slice(0, 10)) ? raw.slice(0, 10) : null;
}

function wholeNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

function money(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
}

export function newPersonalIdKey(): string {
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function personalIdsFrom(value: unknown): PersonalId[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((row) => row && typeof row === 'object')
    .map((row: Record<string, unknown>) => ({
      id: text(row.id) || newPersonalIdKey(),
      type: text(row.type) || '',
      reference: text(row.reference),
      obtainedOn: dateOnly(row.obtainedOn),
      expiresOn: dateOnly(row.expiresOn),
      renewalMonths: wholeNumber(row.renewalMonths),
    }));
}

export function emptyRecord(accountId: string): SubcontractorRecord {
  return {
    accountId,
    contractStartDate: null,
    contractEndDate: null,
    contractTerm: null,
    contractIssued: false,
    contractIssuedOn: null,
    contractSignedOn: null,
    fleetDriver: false,
    vehicleRegistration: null,
    licenceCheckDate: null,
    fleetPolicyIssued: false,
    fleetPolicyIssuedOn: null,
    fleetPolicyVersion: null,
    personalIds: [],
    clothingRequirements: null,
    payTermsNotes: null,
    payStructure: 'not_set',
    payRate: null,
    payStructureNotes: null,
    notes: null,
    updatedAt: null,
  };
}

export function fromDbRecord(row: Record<string, any>): SubcontractorRecord {
  return {
    accountId: String(row.account_id),
    contractStartDate: dateOnly(row.contract_start_date),
    contractEndDate: dateOnly(row.contract_end_date),
    contractTerm: text(row.contract_term),
    contractIssued: !!row.contract_issued,
    contractIssuedOn: dateOnly(row.contract_issued_on),
    contractSignedOn: dateOnly(row.contract_signed_on),
    fleetDriver: !!row.fleet_driver,
    vehicleRegistration: text(row.vehicle_registration),
    licenceCheckDate: dateOnly(row.licence_check_date),
    fleetPolicyIssued: !!row.fleet_policy_issued,
    fleetPolicyIssuedOn: dateOnly(row.fleet_policy_issued_on),
    fleetPolicyVersion: text(row.fleet_policy_version),
    personalIds: personalIdsFrom(row.personal_ids),
    clothingRequirements: text(row.clothing_requirements),
    payTermsNotes: text(row.pay_terms_notes),
    payStructure: PAY_STRUCTURES.has(row.pay_structure) ? row.pay_structure : 'not_set',
    payRate: money(row.pay_rate),
    payStructureNotes: text(row.pay_structure_notes),
    notes: text(row.notes),
    updatedAt: text(row.updated_at),
  };
}

export function toDbRecord(record: SubcontractorRecord): Record<string, unknown> {
  return {
    account_id: record.accountId,
    contract_start_date: dateOnly(record.contractStartDate),
    contract_end_date: dateOnly(record.contractEndDate),
    contract_term: text(record.contractTerm),
    contract_issued: !!record.contractIssued,
    contract_issued_on: record.contractIssued ? dateOnly(record.contractIssuedOn) : null,
    contract_signed_on: record.contractIssued ? dateOnly(record.contractSignedOn) : null,
    fleet_driver: !!record.fleetDriver,
    vehicle_registration: record.fleetDriver ? text(record.vehicleRegistration)?.toUpperCase() ?? null : null,
    licence_check_date: record.fleetDriver ? dateOnly(record.licenceCheckDate) : null,
    fleet_policy_issued: !!record.fleetPolicyIssued,
    fleet_policy_issued_on: record.fleetPolicyIssued ? dateOnly(record.fleetPolicyIssuedOn) : null,
    fleet_policy_version: record.fleetPolicyIssued ? text(record.fleetPolicyVersion) : null,
    personal_ids: record.personalIds
      .filter((row) => text(row.type) || text(row.reference) || row.obtainedOn || row.expiresOn)
      .map((row) => ({
        id: row.id,
        type: text(row.type) || 'ID',
        reference: text(row.reference),
        obtainedOn: dateOnly(row.obtainedOn),
        expiresOn: dateOnly(row.expiresOn),
        renewalMonths: wholeNumber(row.renewalMonths),
      })),
    clothing_requirements: text(record.clothingRequirements),
    pay_terms_notes: text(record.payTermsNotes),
    pay_structure: PAY_STRUCTURES.has(record.payStructure) ? record.payStructure : 'not_set',
    pay_rate: record.payStructure === 'not_set' ? null : money(record.payRate),
    pay_structure_notes: text(record.payStructureNotes),
    notes: text(record.notes),
  };
}

/** Returns the first problem with the record, or null when it can be saved. */
export function validateRecord(record: SubcontractorRecord): string | null {
  const start = dateOnly(record.contractStartDate);
  const end = dateOnly(record.contractEndDate);
  if (start && end && end < start) return 'The contract end date is before the start date.';
  const unnamed = record.personalIds.find((row) => !text(row.type) && (text(row.reference) || row.obtainedOn || row.expiresOn));
  if (unnamed) return 'Give each personal ID a type.';
  return null;
}

/** Today in UK time as YYYY-MM-DD. */
export function ukToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function addMonths(day: string, months: number): string {
  const [y, m, d] = day.split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86400000);
}

/** Flag things this many days before they fall due. */
export const DUE_SOON_DAYS = 30;

export type DueStatus = 'none' | 'ok' | 'soon' | 'overdue';

export function dueStatus(day: string | null, today: string): DueStatus {
  if (!day) return 'none';
  const left = daysBetween(today, day);
  if (left < 0) return 'overdue';
  return left <= DUE_SOON_DAYS ? 'soon' : 'ok';
}

/** When an ID next needs renewing: its expiry, or the obtained date plus the renewal period. */
export function personalIdDueDate(row: PersonalId): string | null {
  if (row.expiresOn) return row.expiresOn;
  if (row.obtainedOn && row.renewalMonths) return addMonths(row.obtainedOn, row.renewalMonths);
  return null;
}

export type RecordAlert = { level: 'danger' | 'warning'; message: string };

/** What needs chasing for one subcontractor, most urgent first. */
export function recordAlerts(record: SubcontractorRecord | null, today: string): RecordAlert[] {
  if (!record) return [{ level: 'warning', message: 'No record yet' }];
  const alerts: RecordAlert[] = [];
  const contract = dueStatus(record.contractEndDate, today);
  if (contract === 'overdue') alerts.push({ level: 'danger', message: 'Contract expired' });
  if (contract === 'soon') alerts.push({ level: 'warning', message: 'Contract ends soon' });
  if (!record.contractIssued) alerts.push({ level: 'warning', message: 'Contract not issued' });
  else if (!record.contractSignedOn) alerts.push({ level: 'warning', message: 'Contract not signed' });
  if (record.fleetDriver && !record.fleetPolicyIssued) {
    alerts.push({ level: 'danger', message: 'Fleet policy not issued' });
  }
  record.personalIds.forEach((row) => {
    const status = dueStatus(personalIdDueDate(row), today);
    if (status === 'overdue') alerts.push({ level: 'danger', message: `${row.type || 'ID'} expired` });
    if (status === 'soon') alerts.push({ level: 'warning', message: `${row.type || 'ID'} due for renewal` });
  });
  return alerts.sort((a, b) => (a.level === b.level ? 0 : a.level === 'danger' ? -1 : 1));
}

export function formatUkDate(day: string | null): string {
  if (!day) return '—';
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
}
